import { useCallback, useEffect, useMemo, useState } from "react";
import VideoPlayer from "./VideoPlayer";
import type {
  Transcription,
  TranscriptionStatus,
} from "./lib/transcription";

const DEFAULT_SRC =
  "https://pub-a817f6012ddd48428cb428fa6408cd7e.r2.dev/Jumanji_scene.mp4";
const TRANSCRIPTION_URL = "/assets/transcription.json";

export default function App() {
  const src = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("src") || DEFAULT_SRC;
    } catch {
      return DEFAULT_SRC;
    }
  }, []);

  // Pass `?failTranscript=1` in the URL to force the fetch into the error
  // state — useful for designing the error UI without breaking the real
  // asset path. Retry will continue to fail while the flag is on.
  const failTranscript = useMemo(() => {
    try {
      return (
        new URLSearchParams(window.location.search).get("failTranscript") ===
        "1"
      );
    } catch {
      return false;
    }
  }, []);

  // The bundled transcription.json is specific to the default Jumanji clip
  // — its timestamps won't match any other video. So we only fetch when the
  // user is playing that exact src. Custom sources via ?src=... get no
  // transcript panel at all (the toggle hides itself when status is null).
  const transcriptAvailable = src === DEFAULT_SRC;

  const [transcription, setTranscription] = useState<Transcription | null>(
    null,
  );
  const [transcriptionStatus, setTranscriptionStatus] = useState<
    TranscriptionStatus | undefined
  >(transcriptAvailable ? "loading" : undefined);

  const loadTranscription = useCallback(() => {
    if (!transcriptAvailable) return;
    let cancelled = false;
    setTranscription(null);
    setTranscriptionStatus("loading");
    const url = failTranscript
      ? "/assets/__forced_failure__.json"
      : TRANSCRIPTION_URL;
    fetch(url)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.statusText)))
      .then((data: Transcription) => {
        if (cancelled) return;
        setTranscription(data);
        setTranscriptionStatus("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn("Failed to load transcription:", err);
        setTranscriptionStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [transcriptAvailable, failTranscript]);

  useEffect(() => loadTranscription(), [loadTranscription]);

  return (
    <div className="h-screen w-screen overflow-hidden bg-black">
      <VideoPlayer
        src={src}
        type="video/mp4"
        transcription={transcription}
        transcriptionStatus={transcriptionStatus}
        onRetryTranscription={loadTranscription}
        onReady={(p) => {
          // eslint-disable-next-line no-console
          console.log("Video player ready", p);
        }}
        onOpenError={(e) => {
          // eslint-disable-next-line no-console
          console.error("Video player error", e);
        }}
      />
    </div>
  );
}
